package com.shadowings.gero.presentation.component

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.wrapContentWidth
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.layout.IntrinsicMeasurable
import androidx.compose.ui.layout.IntrinsicMeasureScope
import androidx.compose.ui.layout.Layout
import androidx.compose.ui.layout.LayoutModifier
import androidx.compose.ui.layout.Measurable
import androidx.compose.ui.layout.MeasureResult
import androidx.compose.ui.layout.MeasureScope
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.Constraints
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.shadowings.gero.data.repository.UniliteralRepository.resFromGardinerCode
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.ui.hieroglyphTint
import org.jetbrains.compose.resources.painterResource

@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
fun MdCWordPreview() {
    AppTheme {
        Surface {
            Column {
                MdCWord(
                    "F34-Z1-X1:F51",
                    rtl = false,
                    cart = false
                )
            }
        }
    }
}


@Composable
fun MdCWord(
    word: String,
    rtl: Boolean,
    cart: Boolean,
    size: Dp = 64.dp,
    testTag: String? = null,
    contentDescription: String? = word,
    hide: Boolean = false,
    override: List<String> = emptyList(),
    // Applied to each flattened cell's own Box, in addition to its normal sizing/hidden-border
    // modifiers — lets a caller hook a specific slot (e.g. to track its on-screen position, or
    // attach an animation) without MdCWord needing to know why. No-op by default.
    cellModifier: (flatIndex: Int) -> Modifier = { Modifier },
) {
    val count = word.split("-").size
    val columns = if (!rtl) {
        word.split("-")
    } else {
        word.split("-").reversed()
    }

    fun Modifier.applySemantics(): Modifier {
        if (testTag.isNullOrBlank() && contentDescription.isNullOrBlank()) return this
        return this.semantics(mergeDescendants = !contentDescription.isNullOrBlank()) {
            if (!testTag.isNullOrBlank()) this.testTag = testTag
            if (!contentDescription.isNullOrBlank()) this.contentDescription = contentDescription
        }
    }

    if (count > 12) {
        val orderedColumns = if (!rtl) columns else columns.reversed()
        val firstHalf = orderedColumns.subList(0, count / 2).joinToString("-")
        val secondHalf = orderedColumns.subList(count / 2, count).joinToString("-")
        val firstHalfCellCount = flattenGardinerCodes(firstHalf, rtl).size
        Column(
            modifier = Modifier.applySemantics(),
            horizontalAlignment = if (rtl) Alignment.End else Alignment.Start,
            verticalArrangement = Arrangement.Center
        ) {
            // contentDescription is announced once on the wrapping node above; the two
            // halves are purely visual continuations of the same word/sentence.
            MdCWord(
                firstHalf, rtl, cart, size, contentDescription = null,
                hide = hide, override = override.take(firstHalfCellCount),
                cellModifier = cellModifier
            )
            MdCWord(
                secondHalf, rtl, cart, size, contentDescription = null,
                hide = hide, override = override.drop(firstHalfCellCount),
                cellModifier = { i -> cellModifier(firstHalfCellCount + i) }
            )
        }
        return
    }

    Row(
        modifier = Modifier.applySemantics(),
        verticalAlignment = Alignment.CenterVertically
    ) {
        if (cart && rtl) {
            Box(modifier = Modifier.width(3.dp).height(size + 16.dp).background(hieroglyphTint))
        }
        Box(
            modifier = Modifier
                .border(
                    width = if (cart) 2.dp else 0.dp,
                    color = if (cart) hieroglyphTint else Color.Transparent,
                    shape = RoundedCornerShape(
                        if (cart) 24.dp else 0.dp,
                        if (cart) 24.dp else 0.dp,
                        if (cart) 24.dp else 0.dp,
                        if (cart) 24.dp else 0.dp
                    )
                )
                .padding(horizontal = 8.dp, vertical = if (cart) 8.dp else 0.dp),
            contentAlignment = Alignment.Center
        ) {
            Layout(
                content = {
                    var columnBaseIndex = 0
                    columns.forEach { columnWord ->
                        MdCColumn(
                            columnWord = columnWord,
                            rtl = rtl,
                            size = size,
                            hide = hide,
                            override = override,
                            baseIndex = columnBaseIndex,
                            cellModifier = cellModifier
                        )
                        columnBaseIndex += flattenGardinerCodes(columnWord, rtl).size
                    }
                }
            ) { measurables, constraints ->
                val defaultHeight = size.roundToPx()

                // 1. Calculate natural widths at default height using maxIntrinsicWidth (without measuring)
                val naturalWidths = measurables.map { measurable ->
                    measurable.maxIntrinsicWidth(defaultHeight)
                }
                val totalWidth = naturalWidths.sum()
                val maxW = constraints.maxWidth

                var finalHeight = defaultHeight

                // If the total width exceeds the available width, scale down the height
                if (maxW in 1..<totalWidth) {
                    val scaleFactor = maxW.toFloat() / totalWidth
                    finalHeight = (defaultHeight * scaleFactor).toInt().coerceAtLeast(1)
                }

                // 3. Measure all measurables EXACTLY ONCE with the final height
                val placeables = measurables.map { measurable ->
                    measurable.measure(
                        Constraints(
                            minHeight = finalHeight,
                            maxHeight = finalHeight
                        )
                    )
                }

                val actualTotalWidth = placeables.sumOf { it.width }

                // 4. Layout placement
                layout(width = actualTotalWidth, height = finalHeight) {
                    var xPosition = 0
                    placeables.forEach { placeable ->
                        placeable.placeRelative(x = xPosition, y = 0)
                        xPosition += placeable.width
                    }
                }
            }
        }
        if (cart && !rtl) {
            Box(modifier = Modifier.width(3.dp).height(size + 16.dp).background(hieroglyphTint))
        }
    }
}

// Optional per-row height/width weight suffixes, e.g. "A12h2:Z1" gives the A12 row
// twice the height share of the (default-weight-1) Z1 row within the same column,
// and "A12w2:Z1" gives A12 twice the width. Both can be combined in either order,
// e.g. "A12h2w1.5:Z1". Lowercase "h"/"w" are used deliberately: real Gardiner codes
// only ever use uppercase letters.
private val ROW_MODIFIER_SUFFIX_REGEX = Regex("^(.*)([hw])(\\d+(?:\\.\\d+)?)$")

private data class RowModifiers(
    val cellsWord: String,
    val heightWeight: Float,
    val widthWeight: Float
)

private fun parseRowModifiers(rowWord: String): RowModifiers {
    var cellsWord = rowWord
    var heightWeight = 1f
    var widthWeight = 1f
    // Strip at most one "h<N>" and one "w<N>" suffix, in either order.
    repeat(2) {
        val match = ROW_MODIFIER_SUFFIX_REGEX.matchEntire(cellsWord) ?: return@repeat
        val (base, kind, numberPart) = match.destructured
        val value = numberPart.toFloatOrNull()?.takeIf { it > 0f } ?: 1f
        when (kind) {
            "h" -> heightWeight = value
            "w" -> widthWeight = value
        }
        cellsWord = base
    }
    return RowModifiers(cellsWord, heightWeight, widthWeight)
}

@Composable
private fun MdCColumn(
    columnWord: String,
    rtl: Boolean,
    size: Dp,
    modifier: Modifier = Modifier,
    hide: Boolean = false,
    override: List<String> = emptyList(),
    baseIndex: Int = 0,
    cellModifier: (flatIndex: Int) -> Modifier = { Modifier },
) {
    val rows = columnWord.split(":")
    Column(
        modifier = modifier.fillMaxHeight(),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        var rowBaseIndex = baseIndex
        rows.forEach { rowWord ->
            val (cellsWord, heightWeight, widthWeight) = parseRowModifiers(rowWord)
            val cells = cellsWord.split("*")
            Row(
                modifier = Modifier
                    .weight(heightWeight)
                    .wrapContentWidth(),
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.CenterVertically
            ) {
                val orderedCells = if (!rtl) cells else cells.reversed()
                orderedCells.forEachIndexed { i, structuralCode ->
                    val flatIndex = rowBaseIndex + i
                    val overrideCode = override.getOrNull(flatIndex)
                    val code = overrideCode ?: structuralCode
                    val isHidden = overrideCode == null && hide
                    resFromGardinerCode(code)?.let { res ->
                        Box(modifier = cellModifier(flatIndex)) {
                            // Sized by its own content (fillMaxHeight + the aspect-ratio cap
                            // below), same as before hide/override existed — narrow glyphs stay
                            // narrow. The wrapping Box has no size modifiers of its own, so it
                            // just takes on whatever width this Image naturally ends up with.
                            Image(
                                modifier = Modifier
                                    .fillMaxHeight()
                                    .maxCellWidth(aspectRatio = rows.size.toFloat() / cells.size.toFloat() * widthWeight)
                                    .padding(2.dp)
                                    .alpha(if (isHidden) 0f else 1f)
                                    .graphicsLayer {
                                        scaleX = if (!rtl) 1f else -1f
                                    },
                                painter = painterResource(res),
                                contentScale = ContentScale.Fit,
                                contentDescription = null,
                                colorFilter = ColorFilter.tint(hieroglyphTint)
                            )
                            if (isHidden) {
                                // A sibling node (not sharing the Image's alpha layer) so the
                                // slot outline stays visible while the glyph itself is invisible;
                                // matchParentSize adapts to whatever width the Image above ended
                                // up with instead of forcing a fixed/full-width slot.
                                Box(
                                    modifier = Modifier
                                        .matchParentSize()
                                        .clip(RoundedCornerShape(6.dp))
                                        .border(
                                            width = 1.5.dp,
                                            color = MaterialTheme.colorScheme.outline.copy(alpha = 0.35f),
                                            shape = RoundedCornerShape(6.dp)
                                        )
                                )
                            }
                        }
                    }
                }
            }
            rowBaseIndex += cells.size
        }
    }
}

private fun Modifier.maxCellWidth(aspectRatio: Float): Modifier = this.then(
    MaxCellWidthModifier(aspectRatio)
)

private class MaxCellWidthModifier(val aspectRatio: Float) : LayoutModifier {
    override fun MeasureScope.measure(
        measurable: Measurable,
        constraints: Constraints
    ): MeasureResult {
        val height = constraints.maxHeight
        val maxW = (height * aspectRatio).toInt().coerceAtLeast(1)
        val placeable = measurable.measure(
            constraints.copy(maxWidth = constraints.maxWidth.coerceAtMost(maxW))
        )
        return layout(placeable.width, placeable.height) {
            placeable.placeRelative(0, 0)
        }
    }

    override fun IntrinsicMeasureScope.maxIntrinsicWidth(
        measurable: IntrinsicMeasurable,
        height: Int
    ): Int {
        val maxW = (height * aspectRatio).toInt().coerceAtLeast(1)
        return measurable.maxIntrinsicWidth(height).coerceAtMost(maxW)
    }

    override fun IntrinsicMeasureScope.minIntrinsicWidth(
        measurable: IntrinsicMeasurable,
        height: Int
    ): Int {
        val maxW = (height * aspectRatio).toInt().coerceAtLeast(1)
        return measurable.minIntrinsicWidth(height).coerceAtMost(maxW)
    }

    override fun IntrinsicMeasureScope.maxIntrinsicHeight(
        measurable: IntrinsicMeasurable,
        width: Int
    ): Int = measurable.maxIntrinsicHeight(width)

    override fun IntrinsicMeasureScope.minIntrinsicHeight(
        measurable: IntrinsicMeasurable,
        width: Int
    ): Int = measurable.minIntrinsicHeight(width)
}

